package com.rosario.juridico.ui.screens

import androidx.compose.animation.*
import androidx.compose.animation.core.*
import com.rosario.juridico.ui.components.CajaResultadoAccionable
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import com.rosario.juridico.ui.components.WebScrollableBox
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.material3.MaterialTheme.colorScheme
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.material3.FilterChip
import androidx.compose.material3.SuggestionChip
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.ui.text.style.TextOverflow
import androidx.navigation.NavController
import com.rosario.juridico.domain.AppMontos
import com.rosario.juridico.ui.theme.Gold400
import juridico5.composeapp.generated.resources.Res
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

// ─────────────────────────────────────────────────────────────────────────────
// MODELOS Y LÓGICA LEY 27.423
// ─────────────────────────────────────────────────────────────────────────────

data class ItemHonorario27423(
    val nombre: String,
    val descripcion: String,
    val categoria: String,
    val tags: List<String>,
    val isRange: Boolean,
    val minValue: Double,
    val maxValue: Double,
    val unit: String, // "UMA", "%", "ESCALA"
    val absolutoMinUma: Double? = null,
    val originalLine: String
)

data class ResultadoLey27423(
    val minUma: Double,
    val maxUma: Double,
    val minPesos: Double,
    val maxPesos: Double,
    val detalle: String,
    val fundamento: String
)

class Ley27423ViewModel {
    var items by mutableStateOf<List<ItemHonorario27423>>(emptyList())
    var searchQuery by mutableStateOf("")
    var selectedItem by mutableStateOf<ItemHonorario27423?>(null)
    var montoBaseStr by mutableStateOf("")
    var valorUmaStr by mutableStateOf(AppMontos.UMA_STR)
    
    var resultado by mutableStateOf<ResultadoLey27423?>(null)

    fun cargarDatos(coroutineScope: CoroutineScope) {
        coroutineScope.launch {
            try {
                val content = Res.readBytes("files/ley_27423_base.txt").decodeToString()
                items = parseLey27423(content)
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }

    private fun parseLey27423(content: String): List<ItemHonorario27423> {
        val list = mutableListOf<ItemHonorario27423>()
        var currentCategory = ""
        
        val rangeUmaRegex = "(\\d+(?:\\.\\d+)?)\\s*UMA\\s*a\\s*(\\d+(?:\\.\\d+)?)\\s*UMA".toRegex()
        val fixedUmaRegex = "(\\d+(?:\\.\\d+)?)\\s*UMA".toRegex()
        val rangePercentRegex = "(\\d+(?:\\.\\d+)?)\\s*%\\s*a\\s*(\\d+(?:\\.\\d+)?)\\s*%".toRegex()
        val fixedPercentRegex = "(\\d+(?:\\.\\d+)?)\\s*%".toRegex()
        val absMinRegex = "\\(Mínimo:\\s*(\\d+(?:\\.\\d+)?)\\s*UMA\\)".toRegex()

        content.lines().forEach { line ->
            val trimmed = line.trim()
            if (trimmed.isEmpty()) return@forEach
            if (trimmed.startsWith("[") && trimmed.endsWith("]") && !trimmed.contains("Tags:")) {
                currentCategory = trimmed.substring(1, trimmed.length - 1).replace("_", " ")
                return@forEach
            }
            if (trimmed.contains(":")) {
                val parts = trimmed.split(":", limit = 2)
                val nombre = parts[0].trim()
                val rest = parts[1].trim()
                val tagMatch = "\\[Tags: (.*?)\\]".toRegex().find(rest)
                val tags = tagMatch?.groupValues?.get(1)?.split(",")?.map { it.trim().lowercase() } ?: emptyList()
                val description = rest.replace("\\[Tags: .*?\\]".toRegex(), "").trim()

                var isRange = false
                var minV = 0.0
                var maxV = 0.0
                var unit = "UMA"
                var absMin: Double? = null
                val usaEscala = description.contains("Escala Art. 21", ignoreCase = true)

                absMinRegex.find(description)?.let {
                    absMin = it.groupValues[1].toDoubleOrNull()
                }

                when {
                    usaEscala -> {
                        unit = "ESCALA"
                        isRange = true
                    }
                    rangeUmaRegex.find(description) != null -> {
                        val match = rangeUmaRegex.find(description)!!
                        minV = match.groupValues[1].toDouble()
                        maxV = match.groupValues[2].toDouble()
                        isRange = true
                        unit = "UMA"
                    }
                    fixedUmaRegex.find(description) != null -> {
                        val match = fixedUmaRegex.find(description)!!
                        minV = match.groupValues[1].toDouble()
                        maxV = minV
                        isRange = false
                        unit = "UMA"
                    }
                    rangePercentRegex.find(description) != null -> {
                        val match = rangePercentRegex.find(description)!!
                        minV = match.groupValues[1].toDouble()
                        maxV = match.groupValues[2].toDouble()
                        isRange = true
                        unit = "%"
                    }
                    fixedPercentRegex.find(description) != null -> {
                        val match = fixedPercentRegex.find(description)!!
                        minV = match.groupValues[1].toDouble()
                        maxV = minV
                        isRange = false
                        unit = "%"
                    }
                }

                list.add(ItemHonorario27423(
                    nombre = nombre,
                    descripcion = description,
                    categoria = currentCategory,
                    tags = tags,
                    isRange = isRange,
                    minValue = minV,
                    maxValue = maxV,
                    unit = unit,
                    absolutoMinUma = absMin,
                    originalLine = trimmed
                ))
            }
        }
        return list
    }

    fun onItemSelected(item: ItemHonorario27423) {
        selectedItem = item
        resultado = null
        montoBaseStr = ""
    }

    fun calcular() {
        val item = selectedItem ?: return
        val montoBase = montoBaseStr.toDoubleOrNull() ?: 0.0
        val vUma = valorUmaStr.toDoubleOrNull() ?: 95626.0
        
        var resMinUma = 0.0
        var resMaxUma = 0.0
        
        when (item.unit) {
            "ESCALA" -> {
                val montoEnUma = if (vUma > 0) montoBase / vUma else 0.0
                val (pMin, pMax) = escalaArt21(montoEnUma)
                resMinUma = montoEnUma * pMin
                resMaxUma = montoEnUma * pMax
            }
            "%" -> {
                val montoEnUma = if (vUma > 0) montoBase / vUma else 0.0
                resMinUma = montoEnUma * (item.minValue / 100.0)
                resMaxUma = montoEnUma * (item.maxValue / 100.0)
            }
            "UMA" -> {
                resMinUma = item.minValue
                resMaxUma = item.maxValue
            }
        }
        
        item.absolutoMinUma?.let { absMin ->
            if (resMinUma < absMin) resMinUma = absMin
            if (resMaxUma < absMin) resMaxUma = absMin
        }
        
        resultado = ResultadoLey27423(
            minUma = resMinUma,
            maxUma = resMaxUma,
            minPesos = resMinUma * vUma,
            maxPesos = resMaxUma * vUma,
            detalle = item.nombre,
            fundamento = "Ley 27.423"
        )
    }

    fun escalaArt21(montoUma: Double): Pair<Double, Double> = when {
        montoUma <= 15.0  -> 0.22 to 0.33
        montoUma <= 45.0  -> 0.20 to 0.26
        montoUma <= 90.0  -> 0.18 to 0.24
        montoUma <= 150.0 -> 0.17 to 0.22
        montoUma <= 450.0 -> 0.15 to 0.20
        montoUma <= 750.0 -> 0.13 to 0.17
        else              -> 0.12 to 0.15
    }

    companion object {
        val instance = Ley27423ViewModel()
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSABLE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

@OptIn(ExperimentalLayoutApi::class, ExperimentalMaterial3Api::class)
@Composable
fun ContenidoLey27423(
    navController: NavController,
    scope: CoroutineScope,
    viewModel: Ley27423ViewModel = Ley27423ViewModel.instance
) {
    var expandirDropdown by remember { mutableStateOf(false) }
    val scrollState = rememberScrollState()
    val thousandSeparator = ThousandSeparatorVisualTransformation()

    LaunchedEffect(Unit) {
        viewModel.cargarDatos(this)
    }

    val filteredItems = remember(viewModel.searchQuery, viewModel.items) {
        if (viewModel.searchQuery.isEmpty()) viewModel.items else {
            val q = viewModel.searchQuery.normalized().trim().lowercase()
            viewModel.items.filter { item ->
                item.nombre.normalized().lowercase().contains(q) ||
                item.tags.any { it.contains(q) }
            }
        }
    }

    var resultadoVisibleConDelay by remember { mutableStateOf(false) }
    val sutilSpring = spring<Float>(dampingRatio = Spring.DampingRatioLowBouncy, stiffness = Spring.StiffnessLow)

    LaunchedEffect(viewModel.resultado) {
        if (viewModel.resultado != null) {
            delay(400)
            resultadoVisibleConDelay = true
            delay(800)
            scrollState.animateScrollTo(scrollState.maxValue, animationSpec = sutilSpring)
        } else {
            resultadoVisibleConDelay = false
        }
    }

    WebScrollableBox(scrollState = scrollState, modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier.fillMaxSize().imePadding().verticalScroll(scrollState).padding(16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
        Text("Honorarios Ley 27.423 (Federal)", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold, color = colorScheme.primary)

        Column(modifier = Modifier.fillMaxWidth()) {
            OutlinedTextField(
                value = viewModel.searchQuery,
                onValueChange = { newValue ->
                    viewModel.searchQuery = newValue
                    expandirDropdown = newValue.isNotEmpty()
                    if (viewModel.selectedItem != null) {
                        viewModel.selectedItem = null
                        viewModel.resultado = null
                        viewModel.montoBaseStr = ""
                    }
                },
                label = { Text("Buscar proceso federal...") },
                modifier = Modifier.fillMaxWidth(),
                trailingIcon = { Icon(Icons.Default.Search, null) },
                singleLine = true,
                shape = RoundedCornerShape(12.dp)
            )

            AnimatedVisibility(
                visible = expandirDropdown && viewModel.searchQuery.isNotEmpty() && filteredItems.isNotEmpty(),
                enter = expandVertically(),
                exit = shrinkVertically()
            ) {
                Card(
                    modifier = Modifier.fillMaxWidth().heightIn(max = 240.dp).padding(top = 4.dp),
                    shape = RoundedCornerShape(12.dp),
                    colors = CardDefaults.cardColors(containerColor = colorScheme.surface),
                    elevation = CardDefaults.cardElevation(defaultElevation = 8.dp),
                    border = BorderStroke(1.dp, colorScheme.outlineVariant)
                ) {
                    Column(modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState())) {
                        filteredItems.take(12).forEach { item ->
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable {
                                        viewModel.searchQuery = item.nombre
                                        expandirDropdown = false
                                        viewModel.onItemSelected(item)
                                    }
                                    .padding(horizontal = 16.dp, vertical = 12.dp)
                            ) {
                                Text(item.nombre, fontWeight = FontWeight.SemiBold, color = colorScheme.onSurface)
                                Text(item.categoria, style = MaterialTheme.typography.labelSmall, color = colorScheme.secondary)
                            }
                            HorizontalDivider(color = colorScheme.surfaceVariant.copy(alpha = 0.5f))
                        }
                    }
                }
            }
        }

        OutlinedTextField(
            value = viewModel.valorUmaStr,
            onValueChange = { viewModel.valorUmaStr = it.filter { c -> c.isDigit() } },
            label = { Text("Valor de 1 UMA ($)") },
            modifier = Modifier.fillMaxWidth(),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            visualTransformation = thousandSeparator,
            leadingIcon = { Icon(Icons.Default.MonetizationOn, null, tint = Gold400) }
        )

        viewModel.selectedItem?.let { item ->
            Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorScheme.primaryContainer.copy(alpha = 0.3f))) {
                Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text("Selección: ${item.nombre}", fontWeight = FontWeight.Bold, color = colorScheme.primary)
                    Text(item.descripcion, style = MaterialTheme.typography.bodySmall)
                    
                    if (item.tags.isNotEmpty()) {
                        Text("Etiquetas de búsqueda:", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold)
                        FlowRow(
                            modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
                            horizontalArrangement = Arrangement.spacedBy(6.dp),
                            verticalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            item.tags.forEach { tag ->
                                SuggestionChip(
                                    onClick = { },
                                    label = { Text(tag, fontSize = 10.sp) },
                                    modifier = Modifier.height(24.dp)
                                )
                            }
                        }
                    }
                }
            }

            if (item.unit == "ESCALA" || item.unit == "%") {
                SeccionLabelHonorarios("Monto del Juicio / Base")
                OutlinedTextField(
                    value = viewModel.montoBaseStr,
                    onValueChange = { viewModel.montoBaseStr = it.filter { c -> c.isDigit() } },
                    label = { Text("$") },
                    modifier = Modifier.fillMaxWidth().resaltadoGuia(viewModel.montoBaseStr.isEmpty()),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    visualTransformation = thousandSeparator
                )
            }

            CalculatorActionLock(
                navController = navController,
                onAction = { viewModel.calcular() }
            ) { onClick ->
                Button(
                    onClick = onClick,
                    modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                    enabled = item.unit == "UMA" || viewModel.montoBaseStr.isNotEmpty()
                ) {
                    Text("Calcular Honorarios (UMA)")
                }
            }
            Spacer(modifier = Modifier.height(40.dp))
        }

        AnimatedVisibility(visible = resultadoVisibleConDelay) {
            viewModel.resultado?.let { res ->
                val textoCopia = """
                    RESUMEN HONORARIOS FEDERALES - LEY 27.423
                    Proceso: ${res.detalle}
                    Honorario: ${res.minUma.toFormato(2)} UMA ($ ${res.minPesos.toFormato(2, true)})
                    ${if (res.maxUma > res.minUma) "Hasta: ${res.maxUma.toFormato(2)} UMA ($ ${res.maxPesos.toFormato(2, true)})" else ""}
                    Fundamento: ${res.fundamento}
                    -------------------------------------------
                    Calculado con www.juridicorosario.com
                """.trimIndent()

                CajaResultadoAccionable(
                    titulo = "RECOMENDACIÓN: MÍNIMO LEGAL",
                    textoParaCompartir = textoCopia
                ) {
                    Text("Cálculo basado en Ley 27.423.", style = MaterialTheme.typography.labelSmall, color = colorScheme.onSecondaryContainer.copy(alpha = 0.7f))

                    Spacer(Modifier.height(4.dp))
                    ResultRow("En UMA:", "${res.minUma.toFormato(2)} UMA", isBold = true)
                    ResultRow("En Pesos:", "$ ${res.minPesos.toFormato(2, true)}", isBold = true, color = colorScheme.primary)

                    HorizontalDivider(Modifier.padding(vertical = 4.dp))

                    if (res.maxUma > res.minUma) {
                        Card(
                            modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                            colors = CardDefaults.cardColors(containerColor = colorScheme.surface.copy(alpha = 0.5f)),
                            border = BorderStroke(1.dp, colorScheme.outlineVariant)
                        ) {
                            Column(modifier = Modifier.padding(8.dp)) {
                                Text("Rango legal: ${res.minUma.toFormato(2)} a ${res.maxUma.toFormato(2)} UMA", style = MaterialTheme.typography.labelSmall)
                                Text("Pesos: $ ${res.minPesos.toFormato(2, true)} a $ ${res.maxPesos.toFormato(2, true)}", style = MaterialTheme.typography.labelSmall)
                            }
                        }
                    }

                    Text("Fundamento: ${res.fundamento}", style = MaterialTheme.typography.labelSmall, color = colorScheme.onSurfaceVariant)
                }
            }
        }

        Spacer(Modifier.height(20.dp))
        var expandirTabla by remember { mutableStateOf(false) }
        Card(onClick = { expandirTabla = !expandirTabla }, modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorScheme.surfaceVariant)) {
            Row(Modifier.padding(14.dp).fillMaxWidth(), Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text("📊 Ver Escalas Art. 21 (Nacional)"); Icon(if (expandirTabla) Icons.Default.ExpandLess else Icons.Default.ExpandMore, null)
            }
        }
        if (expandirTabla) TablaReferenciaLey27423()
    }
}
}

@Composable
fun TablaReferenciaLey27423() {
    Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = colorScheme.surface), border = BorderStroke(1.dp, colorScheme.outline)) {
        Column(modifier = Modifier.padding(14.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text("Escala Art. 21 - Ley 27.423", fontWeight = FontWeight.Bold, color = colorScheme.primary)
            listOf(
                "Hasta 15 UMA" to "22%–33%",
                "16–45 UMA" to "20%–26%",
                "46–90 UMA" to "18%–24%",
                "91–150 UMA" to "17%–22%",
                "151–450 UMA" to "15%–20%",
                "451–750 UMA" to "13%–17%",
                "751+ UMA" to "12%–15%"
            ).forEach { (r, v) ->
                Row(Modifier.fillMaxWidth(), Arrangement.SpaceBetween) {
                    Text(r, style = MaterialTheme.typography.labelSmall)
                    Text(v, style = MaterialTheme.typography.labelSmall, color = colorScheme.primary)
                }
            }
        }
    }
}
